/**
 * Party Manager - Production-Ready Export
 * 
 * Simplified exports as recommended by expert panel
 * Only expose essential public APIs
 */

// ========================================================================
// SERVICE FACTORY - The main entry point
// ========================================================================
export { ServiceFactory } from './infrastructure/factory/ServiceFactory';
export { getConfig } from './config/environment';
export type { PartyManagerConfig } from './config/environment';

// ========================================================================
// EVENT BUS - Real pub/sub implementation
// ========================================================================
export { EventBus, DomainEventBuilder } from './infrastructure/events/EventBus';
export type { DomainEvent, EventHandler, EventSubscription } from './infrastructure/events/EventBus';

// ========================================================================
// CORE DOMAIN TYPES (for consumers who need them)
// ========================================================================

// Authentication types
export type { LoginCommand, LoginResult } from './bounded-contexts/authentication/application/services/AuthenticationService';
export type { RegisterCommand, RegisterResult } from './bounded-contexts/authentication/application/services/RegistrationService';
export type { 
  ChangePasswordCommand, 
  ResetPasswordCommand 
} from './bounded-contexts/authentication/application/services/PasswordService';

// Domain models (for type checking)
export { Person } from './bounded-contexts/parties/domain/aggregates/Person';
export { Organisation } from './bounded-contexts/parties/domain/aggregates/Organisation';
export { Team } from './bounded-contexts/parties/domain/aggregates/Team';

// ========================================================================
// ERROR TYPES (for proper error handling)
// ========================================================================
export {
  BaseError,
  ErrorCode,
  InvalidCredentialsError,
  AccountLockedError,
  SessionExpiredError,
  ValidationError,
  DuplicateEmailError,
  PersonNotFoundError,
  OrganizationNotFoundError,
  DatabaseError,
  ConnectionError,
  ErrorHandler
} from './infrastructure/errors/production-errors';

// ========================================================================
// LOGGING (for production debugging)
// ========================================================================
export { Logger } from './infrastructure/logging/Logger';
export { LogLevel } from './infrastructure/logging/Logger';
export type { LogContext } from './infrastructure/logging/Logger';

/**
 * Quick Start Example:
 * 
 * ```typescript
 * import { ServiceFactory } from '@swoft/party-manager';
 * 
 * // Initialize once at app startup
 * const factory = ServiceFactory.getInstance();
 * await factory.initialize();
 * 
 * // Use services
 * const authService = factory.getAuthenticationService();
 * const result = await authService.login({
 *   email: 'user@example.com',
 *   password: 'password',
 *   rememberMe: false
 * });
 * 
 * // Check health
 * const health = await factory.getHealthStatus();
 * 
 * // Shutdown gracefully
 * await factory.shutdown();
 * ```
 */